import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Avatar, Empty, Spin, Button, Tag } from 'antd';
import {
  ArrowLeftOutlined, LoginOutlined, LogoutOutlined, ClockCircleOutlined,
  EnvironmentOutlined, UserOutlined, ApartmentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import StatCard from '../../components/dashboards/StatCard';
import CommonTable from '../../components/commonTable';
import { useGetAttendanceDetailQuery } from '../../services/attendance';
import '../../components/attendancePage/styles.scss';
import './styles.scss';

/* ─── Types (matches GET /attendance/detail) ────────────────────────── */

interface AttendanceSummary {
  firstCheckIn?:    string;
  lastCheckOut?:    string;
  durationMinutes?: number;
  checkIns?:        number;
  checkOuts?:       number;
  totalPunches?:    number;
  machinesUsed?:    number;
  floors?:          string[];
  status?:          string;
}

interface PunchRecord {
  _id:             string;
  type:            'CheckIn' | 'CheckOut' | string;
  logTime:         string;
  machineId:       string;
  floor:           string;
  recordPurpose:   'in' | 'out' | string;
  machineModel?:   string;
  inputType?:      string;
  punchStatus?:    string;
  rejectionReason?: string | null;
  branchName?:     string;
}

interface AttendanceDetailResponse {
  success:  boolean;
  date:     string;
  member:   string;
  userType: string;
  summary:  AttendanceSummary;
  data:     PunchRecord[];
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

const fmtTime = (t?: string | null) => (t ? dayjs(t).format('hh:mm A') : '—');
const fmtDur  = (m?: number | null) => {
  if (m == null) return '—';
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

/* ─── Component ──────────────────────────────────────────────────────── */

const AttendanceDetail = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date') || dayjs().format('YYYY-MM-DD');

  const { data: resp, isFetching, isError } = useGetAttendanceDetailQuery(
    { userId: userId as string, date },
    { skip: !userId }
  );

  const body    = (resp as unknown as AttendanceDetailResponse) ?? undefined;
  const summary = body?.summary ?? {};
  const punches = body?.data ?? [];
  const branch  = punches[0]?.branchName || '—';
  const floors  = summary.floors ?? [];

  const columns = [
    {
      title: 'Time', key: 'time', width: 130,
      render: (_: unknown, r: PunchRecord) => fmtTime(r.logTime),
    },
    {
      title: 'Type', key: 'type', width: 110,
      render: (_: unknown, r: PunchRecord) =>
        r.recordPurpose === 'in'
          ? <Tag color="green" icon={<LoginOutlined />}>Check-in</Tag>
          : <Tag color="orange" icon={<LogoutOutlined />}>Check-out</Tag>,
    },
    {
      title: 'Floor', key: 'floor', width: 140,
      render: (_: unknown, r: PunchRecord) => <span><EnvironmentOutlined /> {r.floor}</span>,
    },
    {
      title: 'Machine', key: 'machine', width: 200,
      render: (_: unknown, r: PunchRecord) => (
        <span>{r.machineId}{r.machineModel ? ` · ${r.machineModel}` : ''}</span>
      ),
    },
    { title: 'Input', key: 'inputType', width: 100, render: (_: unknown, r: PunchRecord) => r.inputType || '—' },
    {
      title: 'Status', key: 'punchStatus', width: 110,
      render: (_: unknown, r: PunchRecord) =>
        r.punchStatus === 'success'
          ? <Tag color="green">Success</Tag>
          : <Tag color="red" title={r.rejectionReason || undefined}>{r.punchStatus || 'Failed'}</Tag>,
    },
  ];

  return (
    <div className="att-page attendance-detail-page">
      <div className="att-titlebar">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} className="att-back-btn" />
        <h2 className="att-title">Attendance Detail</h2>
        <span className="att-detail-date">{dayjs(date).format('DD MMM YYYY')}</span>
      </div>

      {isFetching ? (
        <div className="att-detail-loading"><Spin size="large" /></div>
      ) : isError || !body ? (
        <Empty description="Could not load attendance for this member/date" style={{ marginTop: 60 }} />
      ) : (
        <>
          <div className="att-detail-member">
            <Avatar size={48} icon={<UserOutlined />} />
            <div>
              <div className="att-detail-member-name">{body.member || '—'}</div>
              <div className="att-detail-member-sub">
                {branch}
                {summary.status && (
                  <Tag className="att-detail-status-tag" color={summary.status.toLowerCase() === 'inside' ? 'green' : 'default'}>
                    {summary.status}
                  </Tag>
                )}
              </div>
            </div>
          </div>

          <div className="att-stats">
            <StatCard title="First Check-in" value={fmtTime(summary.firstCheckIn)} icon={<LoginOutlined />}      color="#10b981" />
            <StatCard title="Last Check-out" value={fmtTime(summary.lastCheckOut)} icon={<LogoutOutlined />}     color="#f59e0b" />
            <StatCard title="Duration"       value={fmtDur(summary.durationMinutes)} icon={<ClockCircleOutlined />} color="#06b6d4" />
            <StatCard title="Total Punches"  value={summary.totalPunches ?? punches.length} icon={<ApartmentOutlined />} color="#8b5cf6" />
            <StatCard title="Machines Used"  value={summary.machinesUsed ?? 0} icon={<UserOutlined />} color="#6366f1" />
            <StatCard title="Floors Visited" value={floors.length} icon={<EnvironmentOutlined />} color="#ec4899" subtitle={floors.join(', ') || undefined} />
          </div>

          {punches.length > 0 ? (
            <CommonTable columns={columns} dataSource={punches} rowKey={(r: PunchRecord) => r._id} scroll={{ x: 780 }} />
          ) : (
            <Empty description="No individual punch data available for this day" style={{ marginTop: 40 }} />
          )}
        </>
      )}
    </div>
  );
};

export default AttendanceDetail;
